import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineViewGrid, HiOutlineCube, HiOutlinePlus, HiOutlineCloud, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineChevronDown, HiOutlineExclamationCircle, HiOutlineLogout, HiOutlineDatabase, HiOutlineClock, HiOutlineCog, HiOutlineViewList, HiOutlineShieldCheck, HiOutlineDocumentReport, HiOutlineGlobe, HiOutlineLightningBolt, HiOutlineServer, HiOutlineUserGroup, HiOutlineSearch, HiOutlineCurrencyRupee, HiOutlineChip, HiOutlineDocumentText } from 'react-icons/hi';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import './Sidebar.css';

function Sidebar({ products, gcpProjects, collapsed, onToggle, onAddProduct, currentPath }) {
  const navigate = useNavigate();
  const { theme, setTheme, THEMES, THEME_META } = useTheme();
  const { signOut, user } = useAuth();
  const [productsExpanded, setProductsExpanded] = useState(true);

  const gcpProjectIds = useMemo(
    () => new Set(gcpProjects.map((p) => p._id)),
    [gcpProjects]
  );

  const isProductDisconnected = (product) => {
    if (!product.cloudRuns?.length) return false;
    return product.cloudRuns.some((cr) => !gcpProjectIds.has(cr.gcpProjectId));
  };

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!collapsed && (
          <div className="sidebar-brand">
            <div className="brand-icon">
              <HiOutlineCloud size={22} />
            </div>
            <div>
              <h2>GCP Tracker</h2>
              <span className="brand-subtitle">Cloud Run Monitor</span>
            </div>
          </div>
        )}
        <button className="collapse-btn" onClick={onToggle}>
          {collapsed ? <HiOutlineChevronRight size={18} /> : <HiOutlineChevronLeft size={18} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        <div
          className={`nav-item ${currentPath === '/' ? 'active' : ''}`}
          onClick={() => navigate('/')}
        >
          <HiOutlineViewGrid size={20} />
          {!collapsed && <span>Dashboard</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/gcp-projects' ? 'active' : ''}`}
          onClick={() => navigate('/gcp-projects')}
        >
          <HiOutlineCloud size={20} />
          {!collapsed && <span>GCP Projects</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/gcp-audit' ? 'active' : ''}`}
          onClick={() => navigate('/gcp-audit')}
        >
          <HiOutlineDocumentReport size={20} />
          {!collapsed && <span>GCP Audit</span>}
        </div>

        <div
          className={`nav-item ${currentPath.startsWith('/team') ? 'active' : ''}`}
          onClick={() => navigate('/team')}
        >
          <HiOutlineUserGroup size={20} />
          {!collapsed && <span>Team</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/search' ? 'active' : ''}`}
          onClick={() => navigate('/search')}
        >
          <HiOutlineSearch size={20} />
          {!collapsed && <span>Search</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/cost-estimation' ? 'active' : ''}`}
          onClick={() => navigate('/cost-estimation')}
        >
          <HiOutlineCurrencyRupee size={20} />
          {!collapsed && <span>Cost Estimation</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/gpu-info' ? 'active' : ''}`}
          onClick={() => navigate('/gpu-info')}
        >
          <HiOutlineChip size={20} />
          {!collapsed && <span>GPU Info</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/logs' ? 'active' : ''}`}
          onClick={() => navigate('/logs')}
        >
          <HiOutlineDocumentText size={20} />
          {!collapsed && <span>Logs</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/mongo-configuration' ? 'active' : ''}`}
          onClick={() => navigate('/mongo-configuration')}
        >
          <HiOutlineCog size={20} />
          {!collapsed && <span>MongoDB Configuration</span>}
        </div>

        <div
          className={`nav-item ${currentPath === '/mongo-observability' ? 'active' : ''}`}
          onClick={() => navigate('/mongo-observability')}
        >
          <HiOutlineDatabase size={20} />
          {!collapsed && <span>MongoDB Observability</span>}
        </div>

        <div className="nav-divider"></div>

        <div className="nav-section">
          {!collapsed && (
            <div className="nav-section-header" onClick={() => setProductsExpanded(!productsExpanded)}>
              <span className="nav-section-title">Products</span>
              <div className="nav-section-actions">
                <button
                  className="add-btn"
                  onClick={(e) => { e.stopPropagation(); onAddProduct(); }}
                  title="Add Product"
                >
                  <HiOutlinePlus size={14} />
                </button>
                <HiOutlineChevronDown
                  size={14}
                  className={`nav-section-chevron ${productsExpanded ? 'expanded' : ''}`}
                />
              </div>
            </div>
          )}
          {collapsed && (
            <div className="nav-item" onClick={onAddProduct}>
              <HiOutlineCube size={20} />
            </div>
          )}

          {!collapsed && productsExpanded && (
            <div className="nav-section-children">
              {products.map((product) => {
                const disconnected = isProductDisconnected(product);
                return (
                  <div
                    key={product._id}
                    className={`nav-item sub-item ${currentPath === `/products/${product._id}` ? 'active' : ''} ${disconnected ? 'disconnected' : ''}`}
                    onClick={() => navigate(`/products/${product._id}`)}
                    title={disconnected ? 'GCP project disconnected — some services may not work' : ''}
                  >
                    {disconnected ? <HiOutlineExclamationCircle size={16} /> : <HiOutlineCube size={16} />}
                    <span>{product.name}</span>
                    <span className={`badge ${disconnected ? 'badge-danger' : ''}`}>{product.cloudRuns?.length || 0}</span>
                  </div>
                );
              })}
              {products.length === 0 && (
                <div className="nav-empty">No products yet</div>
              )}
            </div>
          )}
        </div>

        <div className="nav-divider"></div>

        <div className="nav-section">
          {!collapsed && <div className="nav-section-header"><span className="nav-section-title">GCP Services</span></div>}
          {collapsed ? (
            <>
              <div className={`nav-item ${currentPath === '/vm-instances' ? 'active' : ''}`} onClick={() => navigate('/vm-instances')}>
                <HiOutlineServer size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/cloud-run-services' ? 'active' : ''}`} onClick={() => navigate('/cloud-run-services')}>
                <HiOutlineGlobe size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/cloud-build' ? 'active' : ''}`} onClick={() => navigate('/cloud-build')}>
                <HiOutlineLightningBolt size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/cloud-scheduler' ? 'active' : ''}`} onClick={() => navigate('/cloud-scheduler')}>
                <HiOutlineClock size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/cloud-run-jobs' ? 'active' : ''}`} onClick={() => navigate('/cloud-run-jobs')}>
                <HiOutlineCog size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/cloud-tasks' ? 'active' : ''}`} onClick={() => navigate('/cloud-tasks')}>
                <HiOutlineViewList size={20} />
              </div>
              <div className={`nav-item ${currentPath === '/vpn-details' ? 'active' : ''}`} onClick={() => navigate('/vpn-details')}>
                <HiOutlineShieldCheck size={20} />
              </div>
            </>
          ) : (
            <div className="nav-section-children">
              <div
                className={`nav-item sub-item ${currentPath === '/vm-instances' ? 'active' : ''}`}
                onClick={() => navigate('/vm-instances')}
              >
                <HiOutlineServer size={16} />
                <span>VM Instances</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/cloud-run-services' ? 'active' : ''}`}
                onClick={() => navigate('/cloud-run-services')}
              >
                <HiOutlineGlobe size={16} />
                <span>Cloud Run Services</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/cloud-build' ? 'active' : ''}`}
                onClick={() => navigate('/cloud-build')}
              >
                <HiOutlineLightningBolt size={16} />
                <span>Cloud Build</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/cloud-scheduler' ? 'active' : ''}`}
                onClick={() => navigate('/cloud-scheduler')}
              >
                <HiOutlineClock size={16} />
                <span>Cloud Scheduler</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/cloud-run-jobs' ? 'active' : ''}`}
                onClick={() => navigate('/cloud-run-jobs')}
              >
                <HiOutlineCog size={16} />
                <span>Cloud Run Jobs</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/cloud-tasks' ? 'active' : ''}`}
                onClick={() => navigate('/cloud-tasks')}
              >
                <HiOutlineViewList size={16} />
                <span>Cloud Tasks</span>
              </div>
              <div
                className={`nav-item sub-item ${currentPath === '/vpn-details' ? 'active' : ''}`}
                onClick={() => navigate('/vpn-details')}
              >
                <HiOutlineShieldCheck size={16} />
                <span>VPN Details</span>
              </div>
            </div>
          )}
        </div>
      </nav>

      <div className="sidebar-footer">
        {!collapsed ? (
          <>
            <div className="theme-switcher">
              {THEMES.map(t => (
                <button
                  key={t}
                  className={`theme-btn ${theme === t ? 'active' : ''}`}
                  onClick={() => setTheme(t)}
                  title={THEME_META[t].label}
                >
                  <span className="theme-btn-icon">{THEME_META[t].icon}</span>
                  <span className="theme-btn-label">{THEME_META[t].label}</span>
                </button>
              ))}
            </div>
            <div className="footer-info">
              <span className="footer-label">GCP Projects</span>
              <span className="footer-value">{gcpProjects.length}</span>
            </div>
            <button
              type="button"
              className="sidebar-signout"
              onClick={() => signOut()}
              title="Sign out"
            >
              <HiOutlineLogout size={18} />
              <span>Sign out</span>
            </button>
            {user?.email && <div className="sidebar-user-email" title={user.email}>{user.email}</div>}
          </>
        ) : (
          <div className="sidebar-footer-collapsed">
            <button
              className="theme-cycle-btn"
              onClick={() => {
                const idx = THEMES.indexOf(theme);
                setTheme(THEMES[(idx + 1) % THEMES.length]);
              }}
              title={`Theme: ${THEME_META[theme].label}`}
            >
              {THEME_META[theme].icon}
            </button>
            <button className="theme-cycle-btn" type="button" onClick={() => signOut()} title="Sign out">
              <HiOutlineLogout size={20} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
